import styled, { css } from "styled-components";
import { typography, fontWeights } from "../../../base/styled/typography.style";
import { colors, devices, sizes } from "../../../base/styled/variables.styled";
export const TabLabelWrapper = styled.span `
	display: inline-block;
	position: relative;
	text-align: center;
	font-style: normal;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.1s ease;
	border-radius: 4px;

	.tabs__label__wrapper {
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}

	& svg,
	& img {
		width: 24px;
		height: 24px;
		display: inline-block;
		vertical-align: bottom;
		flex-shrink: 0;
	}

	& svg {
		fill: currentColor;
	}
`;
const tabBaseStyles = css `
	cursor: pointer;
	border: none;
	background-color: transparent;
	transition: border-color 0.2s ease, background-color 0.2s ease;
	flex-shrink: 0;
	position: relative;
	outline: none;
	min-width: 0;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	text-align: center;
`;
const getTabLabelTypography = ($highlighted = false, $isActive = false) => css `
	font-family: ${$highlighted
    ? typography.body2Highlight.fontFamily
    : typography.body2.fontFamily};
	font-size: ${$highlighted
    ? typography.body2Highlight.fontSize
    : typography.body2.fontSize};
	line-height: ${$highlighted
    ? typography.body1Highlight.lineHeight
    : typography.body2.lineHeight};
	text-transform: ${$highlighted ? "none" : "uppercase"};
	font-weight: ${$isActive ? fontWeights.bold : fontWeights.regular};
`;
const getTabLabelColor = (props) => {
    const { $isActive = false, $highlighted = false, $isFocus = false, $isHover = false, $isInverted = false, } = props;
    if ($isInverted) {
        if ($isActive) {
            if ($isFocus) {
                return colors.umbuGreenFigma;
            }
            return colors.umbuGreenFigma;
        }
        else {
            return colors.neutral200;
        }
    }
    else {
        if ($isActive) {
            if ($isFocus) {
                return colors.brand500;
            }
            return colors.brand500;
        }
        else if ($isHover || $isFocus) {
            return colors.neutral800;
        }
        else if ($highlighted) {
            return colors.neutral800;
        }
        else {
            return colors.neutral600;
        }
    }
};
const getTabItemBorderBottom = (props) => {
    const { $isActive = false, $highlighted = false, $isFocus = false, $isHover = false, theme, } = props;
    let borderColor;
    if (theme.name === "dark") {
        if ($isActive) {
            borderColor = colors.umbuGreenFigma;
        }
        else if ($highlighted && $isFocus) {
            borderColor = colors.umbuGreenFigma;
        }
        else if ($isHover) {
            borderColor = colors.umbuGreenFigma;
        }
        else {
            borderColor = colors.neutral200;
        }
    }
    else {
        if ($isActive) {
            borderColor = colors.brand500;
        }
        else if ($highlighted && $isFocus) {
            borderColor = colors.brand500;
        }
        else if ($isHover) {
            borderColor = colors.brand500;
        }
        else {
            borderColor = colors.neutral200;
        }
    }
    return borderColor;
};
const getTabHeight = ($highlighted = false) => css `
	height: ${$highlighted ? "64px" : "48px"};
`;
const Inverted = ($isInverted = false) => css `
	border-bottom: ${$isInverted ? colors.umbuGreenFigma : ""};
	color: ${$isInverted ? colors.umbuGreenFigma : ""};
`;
export const TabItem = styled.button `
	${tabBaseStyles}
	${({ $vertical = false }) => $vertical &&
    css `
			justify-content: flex-start;
		`}
  ${({ $highlighted = false }) => getTabHeight($highlighted)}
  ${({ $isInverted = false }) => Inverted($isInverted)}

  border-bottom: 2px solid ${colors.neutral200};

	${(props) => css `
		${!props.$isActive &&
    css `
			border-bottom: 2px solid ${getTabItemBorderBottom(props)};
		`}

		${props.$isActive &&
    css `
			border-bottom: 2px solid ${getTabItemBorderBottom(props)};
		`}

    ${props.$isActive &&
    props.$isInverted &&
    css `
			border-bottom: 2px solid ${colors.umbuGreenFigma};
			& > ${TabLabelWrapper} {
				color: ${colors.umbuGreenFigma};
			}
		`}

    &:hover:not(:disabled):not([data-active='true']) {
			border-bottom: 2px solid
				${props.theme.name === "dark" || props.$isInverted
    ? colors.umbuGreenFigma
    : colors.brand500};
			& > ${TabLabelWrapper} {
				color: ${props.theme.name === "dark" || props.$isInverted
    ? colors.umbuGreenFigma
    : colors.brand500};
				background-color: transparent;
			}
		}

		&:focus-visible {
			border-bottom: 2px solid ${getTabItemBorderBottom(props)};
		}
	`}

	& > ${TabLabelWrapper} {
		padding: ${({ $highlighted = false }) => $highlighted ? "8px 24px" : "8px 24px"};
		${(props) => getTabLabelTypography(props.$highlighted, props.$isActive)}
		color: ${(props) => getTabLabelColor(props)};
	}

	&:hover:not(:disabled):not([data-active="true"]) {
		& > ${TabLabelWrapper} {
			color: ${(props) => props.theme.name === "dark" || props.$isInverted
    ? colors.umbuGreenFigma
    : colors.brand500};
			background-color: transparent;
		}
	}

	&[data-active="true"] {
		& > ${TabLabelWrapper} {
			color: ${(props) => getTabLabelColor(props)};
		}
	}

	&:disabled {
		cursor: not-allowed;
		border-bottom-color: ${colors.neutral300};
		border-bottom-width: 2px;

		& > ${TabLabelWrapper} {
			color: ${colors.neutral500};
			background-color: transparent;
			font-weight: ${fontWeights.regular};
			font-size: ${typography.legend.fontSize};
			line-height: ${typography.legend.lineHeight};
			text-transform: uppercase;
		}
	}

	&:focus-visible {
		outline: none;

		& > ${TabLabelWrapper} {
			border: 3px solid ${colors.brand300};
			border-radius: 4px;
			box-sizing: border-box;

			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;

			padding: ${({ $highlighted = false }) => $highlighted ? "5px 21px" : "5px 21px"};
			color: ${(props) => getTabLabelColor(props)};
		}
	}

	@media ${devices.mobile} {
		& > ${TabLabelWrapper} {
			padding: 8px 12px;
			font-size: ${typography.body2.fontSize};
			line-height: ${typography.body2.lineHeight};
		}
	}
`;
export const TabsList = styled.div `
	display: flex;
	position: relative;
	padding: 0px;
	background-color: transparent;
	${typography.body2}

	${({ $vertical = false }) => $vertical &&
    css `
			flex-direction: column;
			width: 240px;
			min-width: 240px;
			padding-right: 32px;
			border-bottom: none;
		`}

	${({ $carousel = false }) => $carousel &&
    css `
			overflow-x: auto;
			scroll-behavior: smooth;
			flex-grow: 1;
			min-width: 0;
			padding-left: 0;
			padding-right: 16px;
			-ms-overflow-style: none;
			scrollbar-width: none;
			border-bottom: none;

			&::-webkit-scrollbar {
				display: none;
			}
		`}

	${({ $carousel = false }) => !$carousel &&
    css `
			flex-wrap: nowrap;
			overflow: hidden;
			width: 100%;
		`}

	@media ${devices.tablets} {
		${({ $vertical = false }) => $vertical &&
    css `
				flex-direction: row;
				width: 100%;
				min-width: 0;
				padding-right: 0;
			`}
	}

	@media ${devices.mobile} {
		${({ $vertical = false }) => $vertical &&
    css `
				flex-direction: row;
				width: 100%;
				min-width: 0;
				padding-right: 0;
			`}
		${({ $carousel = false }) => $carousel
    ? css `
						flex-wrap: nowrap;
				  `
    : css `
						flex-direction: row;
						border-bottom: none;
						${TabItem} {
							margin-bottom: 8px;
							border-bottom: 2px solid ${colors.neutral300};
							&[data-active="true"] {
								color: ${colors.brand500};
								font-weight: 700;
								padding: 15px 24px;
								border-bottom: 2px solid ${colors.brand500} !important;
							}
						}
			`}
			${({ $isInverted = false }) => $isInverted &&
    css `
					${TabItem} {
						&[data-active="true"] {
							border-bottom: 2px solid ${colors.umbuGreenFigma} !important;
						}
					}
				`}
	}
`;
export const CarouselWrapper = styled.div `
	display: flex;
	align-items: center;
	box-sizing: border-box;
	position: relative;
	${({ $highlighted = false }) => getTabHeight($highlighted)};
	${({ $isInverted = false }) => Inverted($isInverted)}
	background-color: transparent;
	border-bottom: 1st solid ${colors.neutral300};
	max-width: 100%;
	overflow: hidden;
`;
export const PaginationContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => !['direction'].includes(prop),
}) `
	position: absolute;
	top: 0;
	bottom: 0;
	width: 72px;
	height: 100%;
	display: flex;
	align-items: center;
	z-index: 2;
	pointer-events: none;

	${({ $isInverted, direction }) => css `
		${direction === "left" &&
    css `
			left: 0;
			justify-content: flex-start;
			background: ${$isInverted
        ? css `linear-gradient(90deg, ${colors.backgroundDark} 31.25%, rgba(8, 16, 50, 0) 100%)`
        : css `linear-gradient(90deg, ${colors.neutral000} 31.25%, ${colors.bannerGreyTransparent} 100%)`};

			& > button {
				pointer-events: auto;
			}
		`}

		${direction === "right" &&
    css `
			right: 0;
			justify-content: flex-end;
			background: ${$isInverted
        ? css `linear-gradient(-90deg, ${colors.backgroundDark} 31.25%, rgba(8, 16, 50, 0) 100%)`
        : css `linear-gradient(-90deg, ${colors.neutral000} 31.25%, ${colors.bannerGreyTransparent} 100%)`};

			& > button {
				pointer-events: auto;
			}
		`}
	`}
`;
export const ArrowButton = styled.button `
	border: none;
	background-color: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	width: 56px;
	height: 100%;
	padding: ${({ $direction }) => $direction === "prev" ? "12px 8px 12px 24px" : "12px 24px 12px 8px"};
	border-radius: 4px;
	transition: background-color 0.2s ease;
	pointer-events: auto;

	svg {
		fill: ${colors.brand400};
	}

	color: ${({ $isInverted }) => $isInverted
    ? colors.umbuGreenFigma
    : colors.brand500};

	&:hover:not(:disabled) {
		background-color: ${colors.lightPeriwinkleOpacity30};
	}

	&:focus-visible {
		outline: 2px solid ${colors.brand300};
	}

	&:disabled {
		opacity: 0.3;
		pointer-events: none;
	}
`;
export const TabsContainer = styled.div `
	font-family: ${typography.body1.fontFamily};
	max-width: 100%;
	max-width: ${sizes.desktop};
`;
export const NavButtonsWrapper = styled.div `
	display: flex;
	justify-content: space-between;
	gap: 16px;
	padding: 16px;

	> button {
		white-space: normal;
		height: auto;
		min-height: 56px;
		text-align: center;
	}

	> button span {
		white-space: normal;
	}

	@media ${devices.tablets} {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-areas: "prev next";
		gap: 12px;

		> button {
			width: 100%;
		}
	}

	@media ${devices.mobile} {
		grid-template-columns: 1fr;
		grid-template-areas:
			"next"
			"prev";
		gap: 8px;

		> button {
			min-height: 48px;
			padding: 12px 16px;
		}
	}

	> button:nth-child(1) {
		grid-area: prev;
	}
	> button:nth-child(2) {
		grid-area: next;
	}
`;
export const TabContent = styled.div `
	padding: ${({ $noPadding }) => ($noPadding ? "0" : "16px")};
	min-height: 100px;
	border: 0px solid ${colors.neutral300};
	border-top: none;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	max-width: ${sizes.desktop};
	color: ${({ $isInverted }) => $isInverted ? colors.neutral300 : colors.black};

	@media ${devices.mobile} {
		padding: 8px;
		font-size: 14px;
	}
`;
export const VerticalLayout = styled.div `
	display: grid;
	grid-template-columns: 240px minmax(0, 1fr);
	align-items: start;
	gap: 32px;
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
	overflow: hidden;

	${TabsContainer}, ${TabContent} {
		max-width: 100%;
		min-width: 0;
		overflow: hidden;
	}

	& .image-right {
		padding: 0;
	}

	& .image-left {
		padding: 0;
	}
`;
//# sourceMappingURL=Tabs.style.js.map